/* eslint-disable */
import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import firebase from '../lib/firebase';
import type { User } from '../types/user';

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

interface AuthContextValue extends State {
  platform: 'Firebase';
  createUserWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<any>;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  logout: () => Promise<void>;
  impersonateUser: (userInfo: any) => void;
  stopImpersonating: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

type AuthStateChangedAction = {
  type: 'AUTH_STATE_CHANGED';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type Action = AuthStateChangedAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const reducer = (state: State, action: Action): State => {
  if (action.type === 'AUTH_STATE_CHANGED') {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  }

  return state;
};

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'Firebase',
  createUserWithEmailAndPassword: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  signInWithGoogle: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  impersonateUser: () => {},
  stopImpersonating: () => {}
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => firebase.auth().onAuthStateChanged(async (user) => {
    if (user) {
      let idToken = '';
      let role: any = null;
      await firebase.auth().currentUser.getIdTokenResult()
      .then((idTokenResult) => { 
        role = idTokenResult.claims;
      })
      await firebase.auth().currentUser.getIdToken(true).then((token) => {
        idToken = token;
      });

      let finalUser: any = {
        id: user.uid,
        avatar: user.photoURL,
        email: user.email,
        name: user.displayName,
        token: idToken,
        role : role.role
      };

      const impersonatedUid = localStorage.getItem('impersonate_uid');
      if (impersonatedUid && role.role === 'admin') {
          const infoStr = localStorage.getItem('impersonated_user_info');
          const info = infoStr ? JSON.parse(infoStr) : {};
          finalUser = {
              ...finalUser,
              id: impersonatedUid,
              email: info.email || finalUser.email,
              name: info.displayName || finalUser.name,
              role: info.customClaims?.role || 'user',
              isImpersonating: true
          };
      }

      dispatch({
        type: 'AUTH_STATE_CHANGED',
        payload: {
          isAuthenticated: true,
          user: finalUser
        }
      });
    } else {
      dispatch({
        type: 'AUTH_STATE_CHANGED',
        payload: {
          isAuthenticated: false,
          user: null
        }
      });
    }
  }), [dispatch]);

  const signInWithEmailAndPassword = (
    email: string,
    password: string
  ): Promise<any> => firebase.auth().signInWithEmailAndPassword(email, password);

  const signInWithGoogle = (): Promise<any> => {
    const provider = new firebase.auth.GoogleAuthProvider();

    return firebase.auth().signInWithPopup(provider);
  };

  const createUserWithEmailAndPassword = async (
    email: string,
    password: string
  ): Promise<any> => firebase.auth().createUserWithEmailAndPassword(email, password);

  const logout = async (): Promise<void> => {
    localStorage.removeItem('impersonate_uid');
    localStorage.removeItem('impersonated_user_info');
    await firebase.auth().signOut();
  };

  const impersonateUser = (userInfo: any) => {
    localStorage.setItem('impersonate_uid', userInfo.uid);
    localStorage.setItem('impersonated_user_info', JSON.stringify(userInfo));
    window.location.href = '/app';
  };

  const stopImpersonating = () => {
    localStorage.removeItem('impersonate_uid');
    localStorage.removeItem('impersonated_user_info');
    window.location.href = '/app/admin';
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'Firebase',
        createUserWithEmailAndPassword,
        signInWithEmailAndPassword,
        signInWithGoogle,
        logout,
        impersonateUser,
        stopImpersonating
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
